import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, AlertTriangle, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { partyName } from "@/lib/format";
import { dm } from "@/lib/docReasons";

// Textos (pergunta, tema, porquê) vêm do servidor em p.questions (alerts.py). Aqui só a ordem e o tipo de resposta.
const GROUPS = [
  ["Proprietário · formulário", [["is_family_home"], ["is_heranca"], ["has_minor"], ["has_procuracao"], ["is_rented"]]],
  ["Detetado nos documentos · confirmar", [["seller_company"]]],
  ["Gestora", [["aru", "tri"]]],
];
const BUYER_Q = [["credit_answer", "sn"], ["buyer_company"], ["buyer_procuracao"]];
export const fmtAnswer = (v) => (v === true || v === "sim" ? "Sim" : v === false || v === "nao" ? "Não" : v === "nao_sei" ? "Não sei" : "—");
const opts = (kind) => (kind === "tri" ? [["sim", "Sim"], ["nao", "Não"], ["nao_sei", "Não sei"]] : kind === "sn" ? [["sim", "Sim"], ["nao", "Não"]] : [[true, "Sim"], [false, "Não"]]);
export const KIND = Object.fromEntries([...GROUPS.flatMap(([, qs]) => qs), ...BUYER_Q].map(([k, kind]) => [k, kind]));
export const answerBody = (k, v) => (k === "credit_answer" ? { credit_answer: v, has_credit: v === "sim" } : { [k]: v });
export { opts as answerOptions };

export const OWNER_KEYS = GROUPS.flatMap(([, qs]) => qs.map(([k]) => k));
export const BUYER_KEYS = BUYER_Q.map(([k]) => k);
export const openSpecial = (target = "special-situations") => {
  window.dispatchEvent(new Event("open-special"));
  setTimeout(() => document.querySelector(`[data-testid=${target}]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
};

const SRC = (s) => (!s ? "" : s === "team" ? "consultor" : s === "auto" ? "detetado nos documentos" : "cliente no portal");
const metaOf = (p, k, value, conflict) => {
  const src = (p.special_src || {})[k], at = (p.special_at || {})[k];
  if (conflict) return `consultor: ${fmtAnswer(conflict.team)} · cliente no portal: ${fmtAnswer(conflict.owner)}${at ? ` · ${dm(at)}` : ""}`;
  if (value == null) return null;
  return [SRC(src), at && dm(at)].filter(Boolean).join(" · ") || "respondido";
};

function summary(p) {
  const keys = [...OWNER_KEYS, ...(p.stage !== "angariacao" ? BUYER_KEYS : [])];
  const yes = keys.filter((k) => p[k] === true || p[k] === "sim").map((k) => p.questions?.[k]?.topic || k);
  const open = keys.filter((k) => p[k] == null).length;
  const conf = (p.special_conflicts || []).length;
  const warn = [open && `${open} por responder`, conf && `${conf} a confirmar`].filter(Boolean).join(" · ");
  const nYes = yes.length ? `${yes.length} ${yes.length === 1 ? "situação" : "situações"}` : "";
  if (warn) return [[nYes, warn].filter(Boolean).join(" · "), "amber"];
  if (yes.length) return [`${yes.length} ${yes.length === 1 ? "situação" : "situações"}: ${yes.join(", ")}`, "slate"];
  return ["Sem situações", "green"];
}

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function Question({ k, label, why, kind, value, conflict, meta, owner, onSave }) {
  const [help, setHelp] = useState(false);
  return (
    <div className="py-3" data-testid={`sit-${k}`}>
      <span className="block text-[15px] navy-text">{label}</span>
      <span className={`block text-xs mt-0.5 ${meta ? "text-slate-500" : "font-semibold text-amber-700"}`} data-testid={`sit-meta-${k}`}>
        {meta || "por responder"}{why && <> · <button type="button" onClick={() => setHelp(!help)} className="font-semibold navy-text underline" data-testid={`sit-help-${k}`}>{help ? "Fechar ajuda" : "Saber mais"}</button></>}
      </span>
      {help && why && <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-700" data-testid={`sit-why-${k}`}>{why}</div>}
      <div className="flex gap-1.5 mt-2">
        {opts(kind).map(([v, l]) => (
          <button key={l} type="button" onClick={() => onSave(k, v)} data-testid={`sit-${k}-${l.toLowerCase().replace(" ", "-")}`}
            className={`h-9 px-3.5 text-sm font-semibold rounded-lg border transition-colors ${value === v ? (conflict ? "bg-amber-600 text-white border-amber-600" : "bg-ec-green text-white border-ec-green") : "bg-white border-slate-300 navy-text hover:bg-slate-50"}`}>{l}</button>
        ))}
      </div>
      {conflict && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2" data-testid={`sit-conflict-${k}`}>
          <span className="mr-auto inline-flex items-start gap-1.5"><AlertTriangle size={14} className="mt-0.5 shrink-0" />Respostas diferentes — confirmar com {owner || conflict.owner_name}.</span>
          <button onClick={() => onSave(k, conflict.owner)} className="font-semibold underline" data-testid={`sit-conflict-use-${k}`}>Usar a do cliente</button>
          <button onClick={() => onSave(k, conflict.team)} className="font-semibold underline" data-testid={`sit-conflict-keep-${k}`}>Manter a do consultor</button>
        </div>
      )}
    </div>
  );
}

function Reps({ p, onDone }) {
  const [v, setV] = useState((p.sellers || []).map((s) => s.representative || ""));
  const save = async (i) => {
    if ((p.sellers[i].representative || "") === v[i]) return;
    try { await api.put(`/processes/${p.id}`, { sellers: p.sellers.map((s, j) => (j === i ? { ...s, representative: v[i] } : s)) }); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const proxy = async (i, on) => {
    try { await api.put(`/processes/${p.id}`, { sellers: p.sellers.map((s, j) => (j === i ? { ...s, signs_by_proxy: on } : s)) }); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (p.sellers || []).map((s, i) => (
    <div key={i} className="flex flex-wrap items-center gap-2 pb-2 pl-1">
      <span className="text-xs text-slate-500 w-48 truncate">Quem assina por {partyName(s)}</span>
      <Input value={v[i]} onChange={(e) => setV(v.map((x, j) => (j === i ? e.target.value : x)))} onBlur={() => save(i)} placeholder="Nome do gerente" className="h-8 w-56" data-testid={`sit-rep-${i}`} />
      <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
        <input type="checkbox" checked={!!s.signs_by_proxy} onChange={(e) => proxy(i, e.target.checked)} data-testid={`sit-proxy-${i}`} />Assina por procuração
      </label>
    </div>
  ));
}

function Bank({ p, onSave }) {
  const [v, setV] = useState(p.credit_bank || "");
  return (
    <div className="flex items-center gap-2 pb-2 pl-1">
      <span className="text-xs text-slate-500">Banco</span>
      <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (p.credit_bank || "") && onSave("credit_bank", v)} className="h-8 w-56" data-testid="sit-credit-bank" />
    </div>
  );
}

export function SpecialSituations({ p, onDone, readOnly, defaultOpen = false }) {
  const [open, setOpen] = useState(() => defaultOpen || window.location.hash === "#sec-situacoes");
  const [mine, setMine] = useState({});
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    const f = () => setOpen(true);
    window.addEventListener("open-special", f);
    return () => window.removeEventListener("open-special", f);
  }, []);
  const conflicts = Object.fromEntries((p.special_conflicts || []).map((c) => [c.field, c]));
  const save = async (k, v) => {
    const body = answerBody(k, v);
    setMine((o) => ({ ...o, [k]: v }));
    try { await api.put(`/processes/${p.id}`, body); onDone(); }
    catch (e) { setMine((o) => { const n = { ...o }; delete n[k]; return n; }); toast.error(apiErr(e.response?.data?.detail)); }
  };
  const extras = (k) => (
    <>
      {k === "credit_answer" && p.credit_answer === "sim" && <Bank p={p} onSave={save} />}
      {k === "is_rented" && p.is_rented && (
        <div className="flex items-center gap-2 pb-2 pl-4">
          <span className="text-xs text-slate-500">Início do contrato de arrendamento</span>
          <DateInput value={p.lease_start || ""} onChange={(e) => e.target.value && save("lease_start", e.target.value)} className="w-40 h-8" data-testid="sit-lease-start" />
        </div>
      )}
      {k === "seller_company" && p.seller_company && <Reps p={p} onDone={onDone} />}
    </>
  );
  const val = (k) => (k in mine ? mine[k] : p[k] ?? null);
  const row = ([k, kind]) => (
    <div key={k}>
      <Question k={k} label={p.questions?.[k]?.ask || k} why={p.questions?.[k]?.why} kind={kind} value={val(k)} conflict={conflicts[k]}
        meta={metaOf(p, k, val(k), conflicts[k])} owner={p.sellers?.length === 1 ? partyName(p.sellers[0]) : null} onSave={save} />
      {extras(k)}
    </div>
  );
  const all = [...GROUPS.flatMap(([, qs]) => qs), ...(p.stage !== "angariacao" ? BUYER_Q : [])];
  const isYes = (v) => v === true || v === "sim";
  const topic = (k) => p.questions?.[k]?.topic || k;
  const pending = all.filter(([k]) => val(k) == null || conflicts[k]);
  const yes = all.filter(([k]) => !conflicts[k] && isYes(val(k)));
  const no = all.filter(([k]) => !conflicts[k] && val(k) != null && !isYes(val(k)));
  const answered = [...yes, ...no];
  const lastNo = no.map(([k]) => k).sort((a, b) => String((p.special_at || {})[b] || "").localeCompare(String((p.special_at || {})[a] || "")))[0];
  const saveAll = async () => {
    const ch = Object.entries(edit).filter(([k, v]) => v !== val(k));
    if (!ch.length) { setEdit(null); return; }
    try { await api.put(`/processes/${p.id}`, Object.assign({}, ...ch.map(([k, v]) => answerBody(k, v)))); setEdit(null); onDone(); toast.success("Respostas alteradas · fica no Histórico"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const alterar = !readOnly && <button type="button" onClick={() => setEdit(Object.fromEntries(answered.map(([k]) => [k, val(k)])))} className="text-sm font-semibold navy-text underline shrink-0" data-testid="sit-alterar">Alterar</button>;
  const [sum, tone] = summary(p);
  const shown = open;
  const detected = Object.entries(p.detected || {}).filter(([, v]) => v);
  const PILLC = { amber: "bg-amber-50 text-amber-800", green: "bg-emerald-50 text-emerald-700", slate: "bg-slate-100 text-slate-700" };
  return (
    <div className="bg-white rounded-[16px] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 scroll-mt-24" id="sec-situacoes" data-testid="special-situations">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="special-toggle" aria-expanded={shown}>
        <h3 className="font-heading text-lg navy-text mr-auto">Situações do imóvel</h3>
        <span className={`inline-flex items-center gap-1 text-[13px] font-semibold px-2.5 py-0.5 rounded-full ${PILLC[tone]}`} data-testid="special-summary">{tone === "green" && <Check size={13} />}{sum}</span>
        <ChevronDown size={18} className={`text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {shown && edit && (
        <div className="mt-2" data-testid="sit-edit">
          <div className="text-[15px] font-semibold navy-text">Alterar respostas</div>
          <div className="divide-y divide-slate-100">{answered.map(([k, kind]) => (
            <div key={k} className="flex items-center justify-between gap-3 py-2.5" data-testid={`sit-edit-${k}`}>
              <span className="text-[15px] navy-text">{p.questions?.[k]?.ask || k}</span>
              <div className="flex gap-1.5 shrink-0">{opts(kind).map(([v, l]) => (
                <button key={l} type="button" onClick={() => setEdit((o) => ({ ...o, [k]: v }))} data-testid={`sit-edit-${k}-${l.toLowerCase().replace(" ", "-")}`}
                  className={`h-9 px-3 text-sm font-semibold rounded-lg border transition-colors ${edit[k] === v ? "bg-ec-green text-white border-ec-green" : "bg-white border-slate-300 navy-text hover:bg-slate-50"}`}>{l}</button>
              ))}</div>
            </div>
          ))}</div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setEdit(null)} className="h-10 px-4 rounded-lg border border-slate-300 bg-white font-semibold navy-text hover:bg-slate-50" data-testid="sit-edit-cancel">Cancelar</button>
            <button type="button" onClick={saveAll} className="h-10 px-4 rounded-lg bg-ec-green text-white font-semibold hover:bg-ec-greenDark" data-testid="sit-edit-save">Guardar</button>
          </div>
        </div>
      )}
      {shown && !edit && <fieldset disabled={!!readOnly} className="mt-1" data-testid="special-fieldset">
      {yes.map(([k]) => (
        <div key={k} className="py-3 border-b border-slate-100" data-testid={`sit-yes-${k}`}>
          <div className="flex items-start gap-2.5">
            <span className="w-2 h-2 rounded-full bg-orange-500 mt-2 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2"><span className="text-[15px] font-semibold navy-text mr-auto">{cap(topic(k))}</span>{alterar}</div>
              <div className="text-[13px] text-slate-500 mt-0.5">{[metaOf(p, k, val(k)), p.questions?.[k]?.why].filter(Boolean).join(" · ")}</div>
            </div>
          </div>
          {extras(k)}
        </div>
      ))}
      <div className="divide-y divide-slate-100">{pending.map(row)}</div>
      {no.length > 0 && (
        <div className="pt-3 border-t border-slate-100" data-testid="sit-no-line">
          <p className="text-[15px] navy-text"><b>Sem situações:</b> {no.map(([k]) => { const t = topic(k); return t.charAt(0).toLowerCase() + t.slice(1); }).join(", ")}.</p>
          <div className="flex items-center gap-2 mt-0.5"><span className="text-xs text-slate-500 mr-auto">{metaOf(p, lastNo, val(lastNo))}</span>{alterar}</div>
        </div>
      )}
      {detected.length > 0 && (
        <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2.5 text-[13px] text-slate-700 space-y-1" data-testid="special-detected">
          {detected.map(([k, v]) => <div key={k} data-testid={`sit-detected-${k}`}><b>Detetado nos documentos:</b> {v}</div>)}
        </div>
      )}
      </fieldset>}
    </div>
  );
}
